import React, { useEffect, useState } from 'react';
import { BarChart3, RefreshCw, AlertCircle, MailCheck, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';

const CTA_LABELS = {
  compare_cta: 'Compare Renewals (main CTA)',
  dashboard: 'Go to Dashboard (secondary)',
  '(not set)': 'No utm_content',
};

export default function AdminCampaignReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (refresh = false) => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/admin/campaign-report`, { params: { refresh } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(false); }, []);

  const ga = data?.ga;
  const sent = data?.sent || { variant_a: 0, variant_b: 0 };
  const sentTotal = (sent.variant_a || 0) + (sent.variant_b || 0);

  return (
    <div className="space-y-6" data-testid="admin-campaign-report">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1f3a] flex items-center gap-2">
            <BarChart3 size={22} /> Renewal campaign report
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Emails sent by the nightly renewal cron and their traffic in Google Analytics 4
            (<code className="text-[11px] bg-slate-100 px-1 rounded">utm_campaign=renewal_30d</code>).
          </p>
        </div>
        <button
          onClick={() => load(true)}
          className="btn-outline !py-2 !px-3 text-xs disabled:opacity-60"
          disabled={loading}
          data-testid="campaign-report-refresh"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh from GA4
        </button>
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" /> {error}
        </div>
      )}

      {/* Send counts from our own DB */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <MailCheck size={16} className="text-[#0d3a8a]" />
          <h2 className="font-semibold text-[#0b1f3a]">A/B send counts (from our database)</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Variant A sent" value={sent.variant_a} sub={'"Your policy renews in 30 days - {insurer}"'} />
          <Stat label="Variant B sent" value={sent.variant_b} sub={'"Renew smarter - the average customer saves {avg} EUR"'} />
          <Stat label="Total sent" value={sentTotal} sub="All renewal reminders ever mailed" tone="dark" />
        </div>
      </div>

      {/* GA data */}
      {ga && !ga.configured ? (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6" data-testid="ga-not-configured">
          <h2 className="font-semibold text-amber-900 flex items-center gap-2">
            <AlertCircle size={18} /> Google Analytics 4 not connected yet
          </h2>
          <p className="text-sm text-amber-900 mt-2 leading-relaxed">
            To see open + click stats, add these to <code className="text-[11px] bg-white/60 px-1 py-0.5 rounded">backend/.env</code>:
          </p>
          <pre className="mt-3 bg-white/70 border border-amber-200 rounded-md p-3 text-xs text-slate-800 overflow-x-auto">
{`GA4_PROPERTY_ID=123456789
GA4_CREDENTIALS_FILE=/app/backend/ga-credentials.json`}
          </pre>
          <ol className="mt-4 text-sm text-amber-900 list-decimal ml-4 space-y-1">
            <li>Google Cloud Console -&gt; enable the Google Analytics Data API</li>
            <li>Create a service account, download its JSON key, upload it to <code className="text-[11px] bg-white/60 px-1 py-0.5 rounded">/app/backend/ga-credentials.json</code></li>
            <li>GA4 Admin -&gt; Property Access -&gt; grant the service account email <b>Viewer</b> access</li>
            <li>Restart the backend and refresh this page</li>
          </ol>
        </div>
      ) : ga ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat label={`Sessions (${ga.period_days}d)`} value={ga.total.sessions} tone="dark" />
            <Stat label={`Users (${ga.period_days}d)`} value={ga.total.users} tone="dark" />
            <Stat label={`Events / clicks (${ga.period_days}d)`} value={ga.total.events} tone="dark" />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-semibold text-[#0b1f3a] mb-4">Clicks by CTA position (utm_content)</h2>
            <ReportTable
              rows={ga.by_content}
              keyField="utm_content"
              labelFn={(v) => CTA_LABELS[v] || v}
              testid="ga-by-content"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-semibold text-[#0b1f3a] mb-4">A/B performance by variant (utm_term)</h2>
            <ReportTable
              rows={ga.by_variant}
              keyField="utm_term"
              labelFn={(v) => (v === 'variant_a' ? 'Variant A - "renews in 30 days"' :
                              v === 'variant_b' ? 'Variant B - "average saves EUR"' : v)}
              testid="ga-by-variant"
            />
          </div>

          <p className="text-xs text-slate-500">
            GA4 has a 24-48h data-freshness delay. Data cached for 1 hour per request; hit
            <b> Refresh from GA4</b> to bust the cache.
          </p>
        </>
      ) : null}

      <div className="text-xs text-slate-500 pt-2">
        <a
          href="https://easyspanishinsurance.com/home-insurance?utm_source=renewal_email&utm_medium=email&utm_campaign=renewal_30d&utm_content=compare_cta&utm_term=variant_a"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#0d3a8a] hover:underline"
        >
          Test the tracked link in GA <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const tones = tone === 'dark'
    ? 'bg-[#0b1f3a] text-white border-[#0b1f3a]'
    : 'bg-white text-[#0b1f3a] border-slate-200';
  return (
    <div className={`rounded-2xl border p-5 ${tones}`}>
      <p className={`text-xs uppercase tracking-wider ${tone === 'dark' ? 'text-slate-300' : 'text-slate-500'} font-semibold`}>
        {label}
      </p>
      <p className="text-3xl font-bold mt-1">{(value ?? 0).toLocaleString()}</p>
      {sub && <p className={`text-xs mt-1 ${tone === 'dark' ? 'text-slate-300' : 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}

function ReportTable({ rows = [], keyField, labelFn, testid }) {
  if (!rows.length) {
    return <p className="text-sm text-slate-500 italic">No data in GA yet - reminders are sent nightly and GA takes 24-48h to process events.</p>;
  }
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-slate-500 text-xs uppercase tracking-wider">
            <th className="px-2 py-2 font-semibold">Segment</th>
            <th className="px-2 py-2 font-semibold text-right">Sessions</th>
            <th className="px-2 py-2 font-semibold text-right">Users</th>
            <th className="px-2 py-2 font-semibold text-right">Events</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[keyField]} className="border-t border-slate-100">
              <td className="px-2 py-2 font-medium text-[#0b1f3a]">{labelFn(r[keyField])}</td>
              <td className="px-2 py-2 text-right tabular-nums">{r.sessions.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums">{r.users.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums">{r.events.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
